import React, { useRef, useState } from 'react';
import TaskCard from './TaskCard';
import PropertyPicker from '../PropertyPicker';
import { CARD_PROPERTIES, DEFAULT_PROPERTIES } from './cardProperties';
import {
	areaMeta,
	STATUS_ORDER,
	STATUS_META,
	sortNeedsAction,
	boardStatusColumn,
	GATE_ORDER,
	gateMeta,
} from './taskModel';
import { accentStyle } from './TaskBits';

// Portal Phase C — Board (C2). Kanban grouped by Status / Assignee / Area; the
// columns ARE whatever you group by. Dragging a card to another column writes
// the grouped dimension (status / assignee / area). Drag uses pointer events so
// it works with both mouse and touch; the closedAt stamp on completion is left
// to the stampClosedDate trigger (we never write it by hand).
//
// The Status grouping has ONE column that breaks that symmetry: Parked, last and
// after Completed, holding every task carrying a `gate`. It writes `gate`, not
// `status` — parking is a different field from progress, which is exactly why it
// is not a fourth status (see taxonomy.json gates). Dropping in ASKS what the
// card is waiting on; dragging back out clears the gate and writes the status of
// the column you dropped into, so the round trip is lossless. The writes live in
// WorkTasksModule's handleMove/handleParkChoose; this file decides membership and
// renders the question.

const GROUPS = [
	{ key: 'status', label: 'Status' },
	{ key: 'assignee', label: 'Assignee' },
	{ key: 'area', label: 'Area' },
];

// The Parked lane, appended after the canon statuses so it renders LAST — right
// of Completed. It is the one column whose membership is not the grouped field:
// a parked card is defined by carrying a `gate`, not by its status. Kept here as
// a named constant because three places have to agree on the key (buildColumns,
// inColumn, and handleMove's write in WorkTasksModule).
export const PARKED_COL = 'parked';

// The Completed column is the one lane whose cards are hidden by DEFAULT, and
// the empty column that leaves behind is its PRIMARY state, not an edge case.
// A board accumulates completed cards forever, so showing them by default buries
// the three columns that describe live work behind a wall of strikethrough. But
// the column itself has to stay — it is the drop target for drag-to-complete,
// which means the normal shape of this lane is "visibly droppable and holding
// nothing". Hence a dedicated drop-target empty state rather than the generic
// "Nothing here yet", which reads as absence where this reads as invitation.
//
// The COUNT keeps telling the truth while the cards are hidden: a header reading
// `Completed 24` beside an empty lane is what makes the toggle legible as
// "reveal 24 things" instead of a control with no visible referent.
export const COMPLETED_COL = 'completed';

// Dropping a card on Parked asks what it is waiting ON rather than guessing.
// Every gate value names a specific blocker — a deploy, a decision, Cowork —
// and a drag cannot imply which; guessing would put unlabelled cards in the
// lane, and the lane is only worth having if "parked" reliably means "blocked
// on something". Rendered inside the column, where the card was just dropped,
// rather than as a dialog over the board.
// `gates` defaults to the full canon vocabulary, so a mount that does not pass
// it (or a direct render in a test) behaves exactly as before.
function ParkPrompt({ task, onChoose, onCancel, gates = GATE_ORDER }) {
	return (
		<div className='wt-parkask' role='group' aria-label='What is this waiting on?'>
			<div className='wt-parkask__title'>Waiting on what?</div>
			<div className='wt-parkask__task'>{task.title}</div>
			{gates.map((g) => {
				const meta = gateMeta(g);
				return (
					<button
						key={g}
						type='button'
						className='wt-parkask__opt'
						onClick={() => onChoose(g)}>
						<i className={`fa-sharp-duotone fa-solid ${meta.icon}`} />
						<span>{meta.label}</span>
					</button>
				);
			})}
			<button type='button' className='wt-parkask__cancel' onClick={onCancel}>
				Cancel
			</button>
		</div>
	);
}

// The empty state for a column, written for the case that is now the NORMAL one:
// a Completed lane holding nothing because its cards are hidden. Two jobs the
// generic "Nothing here yet" could not do — it has to read as a live drop target
// (drag-to-complete is the whole reason the lane stays on screen), and it has to
// account for the cards it is concealing, so an empty column is never mistaken
// for a board that has never completed anything.
function CompletedDropTarget({ hidden, isCompletedCol }) {
	if (!isCompletedCol) {
		return <div className='wt-col__empty'>Nothing here yet</div>;
	}
	return (
		<div className='wt-col__empty wt-col__empty--drop'>
			<i className='fa-sharp fa-solid fa-circle-check' aria-hidden='true' />
			<span className='wt-col__droplabel'>Drop here to complete</span>
			{hidden > 0 && (
				<span className='wt-col__hiddenote'>
					{hidden} completed {hidden === 1 ? 'task' : 'tasks'} hidden
				</span>
			)}
		</div>
	);
}

function buildColumns(groupBy, peopleInUse, areasInUse) {
	if (groupBy === 'assignee') {
		return [
			...peopleInUse.map((p) => ({
				key: p.uid,
				label: p.name,
				accent: `var(${p.colorVar})`,
			})),
			{ key: 'unassigned', label: 'Unassigned', accent: 'var(--text-muted)' },
		];
	}
	if (groupBy === 'area') {
		return areasInUse.map((a) => ({
			key: a,
			label: areaMeta(a).label,
			accent: `var(--area-${a})`,
		}));
	}
	return [
		...STATUS_ORDER.map((s) => ({
			key: s,
			label: STATUS_META[s].label,
			accent: `var(--status-${s === 'in_progress' ? 'progress' : s === 'completed' ? 'done' : 'pending'})`,
		})),
		{ key: PARKED_COL, label: 'Parked', accent: 'var(--text-muted)' },
	];
}

function inColumn(task, groupBy, key) {
	// Precedence (completed > gate > status) lives in taskModel so it is testable
	// on the import-pure side and can't drift from the urgency view's copy.
	if (groupBy === 'status') return boardStatusColumn(task) === key;
	if (groupBy === 'area') return (task.area || 'none') === key;
	// assignee
	if (key === 'unassigned') return !(task.assigneeUids || []).length;
	return (task.assigneeUids || []).includes(key);
}

function BoardView({
	roots,
	childrenByParent,
	peopleInUse,
	areasInUse,
	groupBy,
	setGroupBy,
	now,
	selected,
	onSelectedChange,
	// The catalog this space offers. Defaults to the full studio set so a mount
	// that predates per-space fields (or a direct render in a test) is unchanged.
	propertyCatalog = CARD_PROPERTIES,
	// The gate vocabulary this space offers when parking a card. Same default
	// reasoning as propertyCatalog above.
	offeredGates = GATE_ORDER,
	projectsById,
	parkPrompt,
	onParkChoose,
	onParkCancel,
	onMove,
	onOpen,
	// Completed-card visibility. Defaults to hidden so a mount that predates the
	// preference (or a direct render in a test) gets the new default rather than
	// the old always-visible behaviour; onShowCompletedChange is optional so a
	// read-only mount still renders the toggle in a coherent state.
	showCompleted = false,
	onShowCompletedChange,
}) {
	const columns = buildColumns(groupBy, peopleInUse, areasInUse);
	const [drag, setDrag] = useState(null); // { task, x, y, overKey } — drives ghost + dropzone render only
	const startRef = useRef(null);
	const dropRef = useRef(null); // live drop target { task, overKey }; read by onPointerUp (state is stale in that closure)
	const childrenOf = (id) => childrenByParent.get(id) || [];

	const onPointerDown = (e, task) => {
		if (e.button != null && e.button !== 0) return;
		startRef.current = {
			task,
			startX: e.clientX,
			startY: e.clientY,
			active: false,
			pointerId: e.pointerId,
		};
		window.addEventListener('pointermove', onPointerMove);
		window.addEventListener('pointerup', onPointerUp, { once: true });
	};

	const onPointerMove = (e) => {
		const s = startRef.current;
		if (!s) return;
		if (!s.active) {
			const moved =
				Math.abs(e.clientX - s.startX) + Math.abs(e.clientY - s.startY);
			if (moved < 7) return;
			s.active = true;
		}
		e.preventDefault?.();
		const el = document.elementFromPoint(e.clientX, e.clientY);
		const col = el && el.closest('[data-col-key]');
		const overKey = col ? col.getAttribute('data-col-key') : null;
		// The ref carries the drop truth across the async pointer sequence; the
		// `drag` state below is a stale null inside onPointerUp's closure.
		dropRef.current = { task: s.task, overKey };
		setDrag({ task: s.task, x: e.clientX, y: e.clientY, overKey });
	};

	const onPointerUp = () => {
		window.removeEventListener('pointermove', onPointerMove);
		const s = startRef.current;
		const d = dropRef.current;
		startRef.current = null;
		dropRef.current = null;
		setDrag(null);
		if (!s) return;
		if (!s.active) {
			// A tap (no drag) opens the task.
			onOpen?.(s.task);
			return;
		}
		if (d && d.overKey && !inColumn(s.task, groupBy, d.overKey)) {
			onMove?.(s.task, groupBy, d.overKey);
		}
	};

	return (
		<div>
			<div className="wt-board__hint">
				<div className="wt-seg" role="group" aria-label="Group by">
					{GROUPS.map((g) => (
						<button
							key={g.key}
							type="button"
							className={`wt-seg__btn${groupBy === g.key ? ' is-active' : ''}`}
							onClick={() => setGroupBy(g.key)}>
							{g.label}
						</button>
					))}
				</div>
				<span className="wt-board__swipe">
					<i className="fa-sharp fa-solid fa-arrow-right-long" /> Swipe to see
					more
				</span>
				{/* The catalog, defaults and the suppression note are the board's
				    knowledge, not the picker's — passed in since the picker became
				    shared with the dashboard (2026-07-26). */}
				<PropertyPicker
					catalog={propertyCatalog}
					defaults={DEFAULT_PROPERTIES.filter((k) =>
						propertyCatalog.some((p) => p.key === k)
					)}
					selected={selected}
					onChange={onSelectedChange}
					title="Show on cards"
					triggerClassName="wt-seg__btn"
					note="A property is hidden when the board is grouped by it — the column already says so."
				/>
			</div>

			{/* is-fitted opts this board into sharing the row instead of scrolling —
			    but only when the column count is small enough that sharing it is an
			    improvement. Status always yields four (three lifecycle + Parked);
			    grouping by area or assignee can yield a dozen, and flexing those
			    would crush every one of them. The WIDTH half of the decision is a
			    container query in CSS, which the sidebar's collapsed state changes
			    without changing the viewport — so a viewport media query would get
			    it wrong exactly when the user has just made room. */}
			<div
				className={`wt-board${columns.length <= 4 ? ' is-fitted' : ''}`}>
				{columns.map((col) => {
					const cards = sortNeedsAction(
						roots.filter((t) => inColumn(t, groupBy, col.key)),
						now
					);
					const isOver = drag && drag.overKey === col.key;
					// Scoped to the Status grouping deliberately: Completed is a
					// column only there. Grouping by area or assignee has no lane
					// meaning "done", so there is nothing to hide and nowhere to
					// hang the toggle — those groupings are untouched.
					const isCompletedCol =
						groupBy === 'status' && col.key === COMPLETED_COL;
					const hidingCompleted = isCompletedCol && !showCompleted;
					return (
						<section
							key={col.key}
							className={`wt-col${isOver ? ' is-dropzone' : ''}`}
							data-col-key={col.key}
							style={{ '--col-accent': col.accent }}>
							<header className="wt-col__head">
								<span className="wt-col__dot" />
								<span className="wt-col__name">{col.label}</span>
								<span className="wt-col__count">{cards.length}</span>
								{/* ALWAYS RENDERED, never hover-revealed: the portal's primary
								    user has keratoconus and the installed PWA has no hover at
								    all, so this is muted with a COLOUR token and brightens on
								    hover/focus (WorkTasks.css). Suppressed only when there is
								    genuinely nothing to reveal and nothing to put back. */}
								{isCompletedCol && (cards.length > 0 || showCompleted) && (
									<button
										type="button"
										className="wt-col__toggle"
										aria-pressed={showCompleted}
										onClick={() => onShowCompletedChange?.(!showCompleted)}>
										<i
											className={`fa-sharp fa-solid ${
												showCompleted ? 'fa-eye-slash' : 'fa-eye'
											}`}
											aria-hidden='true'
										/>
										<span>{showCompleted ? 'Hide' : 'Show'}</span>
									</button>
								)}
							</header>
							{parkPrompt && col.key === PARKED_COL && (
								<ParkPrompt
									task={parkPrompt}
									onChoose={onParkChoose}
									onCancel={onParkCancel}
									gates={offeredGates}
								/>
							)}
							{hidingCompleted || cards.length === 0 ? (
								<CompletedDropTarget
									hidden={hidingCompleted ? cards.length : 0}
									isCompletedCol={isCompletedCol}
								/>
							) : (
								cards.map((task) => (
									<TaskCard
										key={task.id}
										task={task}
										groupBy={groupBy}
										childrenOf={childrenOf}
										now={now}
										selected={selected}
										projectsById={projectsById}
										dragging={drag?.task?.id === task.id}
										onPointerDown={onPointerDown}
									/>
								))
							)}
						</section>
					);
				})}
			</div>

			{/* floating drag ghost */}
			{drag && drag.x != null && (
				<div
					className="wt-card wt-dragghost"
					style={{
						...accentStyle(drag.task.area),
						left: drag.x - 120,
						top: drag.y - 24,
					}}>
					<div className="wt-card__title">{drag.task.title}</div>
				</div>
			)}
		</div>
	);
}

export default BoardView;
